import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Textarea from '~/components/Textarea'

const TextareaPage: Component = () => {
  const [content, setContent] = createSignal('')

  return (
    <>
      <Title>Textarea - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Textarea</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Entrada de texto multilínea con inteligencia elástica. El componente Textarea ofrece redimensionamiento automático, soporte para labels dinámicos y validación integrada para descripciones
            extensas.
          </p>
        </div>

        <div class="space-y-12">
          {/* Configuraciones de Entrada */}
          <Card>
            <Card.Header
              title="Entrada Descriptiva"
              subtitle="Variantes de estado y comportamientos dinámicos para captura de texto enriquecido."
              icon={<Icon icon="material-symbols-light:music-note-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-10 bg-surface-muted rounded-3xl border border-border border-dashed">
                <div class="space-y-6">
                  <Badge variant="soft" color="info" size="sm">
                    Auto-resize Active
                  </Badge>
                  <Textarea label="Descripción del Proyecto" placeholder="Detalla los objetivos aquí..." value={content()} onInput={e => setContent(e.currentTarget.value)} />
                  <div class="flex justify-end">
                    <div class="text-[10px] font-mono text-foreground-subtle uppercase tracking-widest">Chars: {content().length}</div>
                  </div>
                </div>
                <div class="space-y-6">
                  <Badge variant="soft" color="danger" size="sm">
                    Validation Error
                  </Badge>
                  <Textarea label="Observaciones Críticas" placeholder="Campo con error..." error="La descripción debe contener al menos 10 caracteres." />
                  <Textarea label="Modo Lectura" disabled value="Este registro está bloqueado para edición por políticas de seguridad." />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Elasticidad y Scroll */}
          <Card>
            <Card.Header
              title="Comportamiento Elástico"
              subtitle="Ajuste automático de altura hasta 500px antes de activar scroll persistente."
              icon={<Icon icon="material-symbols-light:straighten-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="max-w-3xl mx-auto space-y-6">
                <Textarea
                  label="Manifiesto del Sistema"
                  rows={8}
                  value={`El diseño elástico permite que el componente crezca orgánicamente con el contenido del usuario. 
                                    \n1. Altura mínima base definible por rows.
                                    \n2. Expansión fluida basada en saltos de línea.
                                    \n3. Límite de seguridad de 500px para evitar desbordamientos infinitos.
                                    \n4. Barra de scroll integrada con estética premium.`}
                />
                <div class="p-4 bg-warning-soft rounded-2xl border border-warning/25 flex items-start gap-4">
                  <Icon icon="material-symbols-light:info-outline" class="text-amber-500 mt-0.5" size={20} />
                  <p class="text-xs text-on-warning-soft leading-relaxed italic">
                    El label flotante utiliza un sistema de posicionamiento absoluto que garantiza legibilidad incluso cuando el usuario realiza scroll dentro del área de texto.
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Tamaños */}
          <Card>
            <Card.Header
              title="Tamaños"
              subtitle="Cinco densidades alineadas con Select y Button: xs, sm, md, lg y xl."
              icon={<Icon icon="material-symbols-light:straighten" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-4 p-10 bg-surface-muted rounded-2xl border border-border">
                <Textarea size="xs" label="Extra Small" placeholder="xs" />
                <Textarea size="sm" label="Small" placeholder="sm" />
                <Textarea size="md" label="Medium" placeholder="md" />
                <Textarea size="lg" label="Large" placeholder="lg" />
                <Textarea size="xl" label="Extra Large" placeholder="xl" />
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente Textarea."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">label</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Etiqueta flotante superior.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4 italic text-xs">'xs' | 'sm' | 'md' | 'lg' | 'xl'</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 italic">Padding vertical y tipografía (36/40/48/56/64px).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">value</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Valor controlado del componente.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">error</td>
                      <td class="px-6 py-4 italic text-xs">string | boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Estado de error visual y mensaje de validación.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">disabled</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Bloquea la edición y cambia la opacidad.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default TextareaPage
